import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useAppSelector } from '../store';
import eventBus, { ESocketEvent } from '../socket/eventBus';
import { IPublicWSData, IPublicWSResponse } from '../interfaces/pair';

const URL = process.env.REACT_APP_SERVICE_SOCKET ?? '';

interface SocketContextProps {}

const PublicSocketContext = createContext<SocketContextProps | undefined>(undefined);

interface SocketProviderProps {
  children: ReactNode;
}

const reconnectInterval = 3000;
const maxReconnectAttempts = 10;

type OrderBookTopic = {
  asset: string;
  tickSize: number;
};

const getOrderBookTopic = (baseToken?: string, tickSize?: number): OrderBookTopic | null => {
  if (!baseToken || tickSize == null || Number.isNaN(Number(tickSize))) {
    return null;
  }

  return {
    asset: baseToken,
    tickSize: Number(tickSize),
  };
};

const isMatchingOrderBookTopic = (
  data: IPublicWSResponse<IPublicWSData>,
  expectedTopic: OrderBookTopic | null,
) => {
  if (!expectedTopic || data?.topic?.type !== 'orderbook') {
    return false;
  }

  return (
    data.topic.asset === expectedTopic.asset &&
    Number(data.topic.extra?.tickSize) === expectedTopic.tickSize
  );
};

export const PublicSocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const currentPair = useAppSelector((state) => state.pairs.currentPair);
  const selectedSize = useAppSelector((state) => state.pairs.selectedSize);
  const [isSocketInitialized, setIsSocketInitialized] = useState(false);

  const reconnectAttemptsRef = useRef(0);
  const isReconnectingRef = useRef(false); // Prevent multiple calls

  const webSocketRef = useRef<WebSocket | null>(null);
  const activeOrderBookTopicRef = useRef<OrderBookTopic | null>(null);

  const attemptReconnect = useCallback(() => {
    if (isReconnectingRef.current) {
      console.log('Reconnect already in progress. Skipping...');
      return;
    }
    isReconnectingRef.current = true;

    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      console.log('Max reconnect attempts reached. Please refresh the page to try again.');
      isReconnectingRef.current = false;
      window.location.reload(); // Reload the page when the reconnect limit is exceeded.
      return;
    }

    reconnectAttemptsRef.current += 1;
    console.log(`Reconnecting attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts}...`);

    setTimeout(() => {
      isReconnectingRef.current = false;
      webSocketRef.current = null;
      initWebSocket();
    }, reconnectInterval);
  }, [maxReconnectAttempts, reconnectInterval]);

  const initWebSocket = useCallback(() => {
    if (webSocketRef.current) return;
    console.log('URL', URL);
    const newWs = new WebSocket(`${URL}/api-gateway/v1/public/ws`);
    webSocketRef.current = newWs;

    newWs.onopen = () => {
      reconnectAttemptsRef.current = 0;
      setIsSocketInitialized(true);
    };

    newWs.onmessage = (event) => {
      const data: IPublicWSResponse<IPublicWSData> = JSON.parse(event.data);

      if (data?.topic?.type === 'market_data' && data.success) {
        eventBus.dispatch(ESocketEvent.MARKET_DATA, data);
      }

      if (data?.topic?.type === 'orderbook' && data.success) {
        if (isMatchingOrderBookTopic(data, activeOrderBookTopicRef.current)) {
          eventBus.dispatch(ESocketEvent.ORDER_BOOK, data);
        }
      }
    };

    newWs.onclose = () => {
      if (webSocketRef.current) {
        console.log('ws closed by server');
      } else {
        console.log('ws closed by app component unmount');
        return;
      }
      if (isReconnectingRef.current) {
        console.log('Reconnection in progress, ignoring close handler.');
        return;
      }
      console.log('WebSocket connection closed. Attempting to reconnect...');
      setIsSocketInitialized(false);
      attemptReconnect();
    };

    newWs.onerror = (error) => {
      console.error('WebSocket error:', error);
      newWs.close();
    };

    return () => {
      webSocketRef.current = null;
      newWs.close();
    };
  }, [attemptReconnect]);

  useEffect(() => {
    return initWebSocket();
  }, [initWebSocket]);

  useEffect(() => {
    activeOrderBookTopicRef.current = getOrderBookTopic(
      currentPair?.baseToken,
      selectedSize ?? currentPair?.config?.defaultTickSize,
    );
  }, [currentPair?.baseToken, currentPair?.config?.defaultTickSize, selectedSize]);

  useEffect(() => {
    const orderBookTopic = getOrderBookTopic(
      currentPair?.baseToken,
      selectedSize ?? currentPair?.config?.defaultTickSize,
    );
    const marketDataAsset = currentPair?.baseToken;

    if (
      webSocketRef.current &&
      webSocketRef.current.readyState === WebSocket.OPEN &&
      orderBookTopic &&
      marketDataAsset
    ) {
      const orderBookRequest = {
        topic: {
          type: 'orderbook',
          asset: orderBookTopic.asset,
          extra: {
            tickSize: orderBookTopic.tickSize,
          },
        },
        action: 'subscribe',
      };
      const marketDataRequest = {
        topic: {
          type: 'market_data',
          asset: marketDataAsset,
        },
        action: 'subscribe',
      };
      webSocketRef.current.send(JSON.stringify(orderBookRequest));
      webSocketRef.current.send(JSON.stringify(marketDataRequest));
    }

    return () => {
      if (
        webSocketRef.current &&
        webSocketRef.current.readyState === WebSocket.OPEN &&
        orderBookTopic &&
        marketDataAsset
      ) {
        const orderBookRequest = {
          topic: {
            type: 'orderbook',
            asset: orderBookTopic.asset,
            extra: {
              tickSize: orderBookTopic.tickSize,
            },
          },
          action: 'unsubscribe',
        };
        const marketDataRequest = {
          topic: {
            type: 'market_data',
            asset: marketDataAsset,
          },
          action: 'unsubscribe',
        };
        webSocketRef.current.send(JSON.stringify(orderBookRequest));
        webSocketRef.current.send(JSON.stringify(marketDataRequest));
      }
    };
  }, [currentPair, selectedSize, isSocketInitialized]);

  return <PublicSocketContext.Provider value={{}}>{children}</PublicSocketContext.Provider>;
};

export const usePublicSocket = (): SocketContextProps => {
  const context = useContext(PublicSocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};
